/** Auto Sourcing default profile — the single model shared by Basic and Advanced UI. */
export const EMPTY_AUTO_SOURCING_PROFILE = {
  name: "",
  mode: "basic",

  scope: {
    source_ids: [],
    categories: [],
    category_urls: [],
    selected_categories: [],
    custom_category_query: null,
    source_scope: {},
    range_mode: "automatic",
    range_start: null,
    range_end: null,
    freshness_hours: 0,
  },

  match_strategy: {
    require_identifier: false,
    allow_model_match: true,
    allow_title_match: false,
    use_image_evidence: true,
    include_review: true,
    allowed_statuses: null,
  },

  adjustments: {
    fixed_source_cost: null,
    store_discount_percent: 0,
    cashback_percent: 0,
    gift_card_percent: 0,
    sales_tax_percent: 0,
    source_shipping: 0,
  },

  exclusions: {
    source_oos: true,
    walmart_oos: true,
    pack_mismatch: true,
    variant_mismatch: true,
    condition_mismatch: true,
    review_matches: false,
  },

  title_rules: {
    source_contains: [],
    source_excludes: [],
    walmart_contains: [],
    walmart_excludes: [],
    brand_include: [],
    brand_exclude: [],
  },

  costs: {
    prep_cost: 0,
    inbound_shipping: 0,
    packaging: 0,
    other_cost: 0,
  },

  profitability: {
    min_gross_spread: null,
    min_gross_roi: null,
    min_estimated_profit: null,
    min_estimated_roi: null,
    min_margin: null,
    max_acquisition_cost: null,
  },

  demand: {
    require_signal: false,
    min_monthly_sold: null,
    max_monthly_sold: null,
    min_rating: null,
    min_reviews: null,
  },

  competition: {
    min_seller_count: null,
    max_seller_count: null,
    seller_type: "any",
  },

  availability: {
    source: "any",
    walmart: "any",
    pack_match: "any",
    variant_match: "any",
  },

  scan_limits: {
    // custom = stop after max_products_per_source; all = no arbitrary product ceiling
    // (budget / duration / category exhaustion + safety brake still apply).
    products_per_source_mode: "custom",
    max_products_per_source: 40,
    max_sources: 1,
    // Scrape.do: ~1 PLP + PDPs per category across Toys/Beauty/Tech/… alternates
    // before Walmart UPC pre-verification evaluates the batch.
    max_provider_requests: 60,
    max_duration_minutes: 30,
  },

  schedule: {
    mode: "once",
    interval_minutes: null,
  },

  enabled: false,
};

/**
 * Shared preset policy — ONE source of truth for the built-in preset values.
 * Backend Pydantic validation remains the authority on allowed ranges; these
 * are the documented frontend defaults that visibly prefill the form.
 *
 * Numeric thresholds reuse the canonical Ezisell economics vocabulary from
 * app/calc.py. Stage G WINNING acceptance floors are $3 / MIN_ROI=20%. The
 * customer "Winning" preset stays stricter at TARGET_ROI=30 / $3.
 */
export const PRESET_POLICY = {
  // Customer Winning preset (stricter than Stage G acceptance MIN_ROI=20).
  baseRoi: 30,
  highRoi: 45,
  baseProfit: 3,
  // Stage G acceptance floor (classify default when profile omits ROI).
  acceptanceRoi: 20,
  // Documented low-competition default (few marketplace sellers).
  maxSellers: 3,
  // Documented demand-signal defaults (rating + review floor).
  minRating: 4.0,
  minReviews: 100,
};

function profitTargets(roi, profit) {
  return {
    min_estimated_roi: roi,
    min_estimated_profit: profit,
    min_margin: null,
    max_acquisition_cost: null,
  };
}

/**
 * Built-in presets — each produces a REAL profile patch that visibly changes
 * form values. Selecting a preset does NOT invent backend-inapplicable fields.
 */
export const BUILTIN_PRESETS = [
  {
    id: "all",
    name: "All",
    description: "No filters applied — shows every persisted opportunity.",
    patch: {
      match_strategy: {
        require_identifier: false,
        allow_model_match: true,
        allow_title_match: false,
        include_review: true,
        allowed_statuses: null,
      },
      exclusions: { review_matches: false },
      profitability: profitTargets(null, null),
      demand: { require_signal: false, min_rating: null, min_reviews: null },
      competition: { max_seller_count: null },
    },
  },
  {
    id: "winning",
    name: "Winning",
    description:
      "Identifier-verified matches only, with the canonical profit targets.",
    patch: {
      match_strategy: {
        require_identifier: true,
        allow_title_match: false,
        allow_model_match: true,
        include_review: true,
        allowed_statuses: null,
      },
      exclusions: {
        source_oos: true,
        walmart_oos: true,
        pack_mismatch: true,
        variant_mismatch: true,
        condition_mismatch: true,
        review_matches: false,
      },
      profitability: profitTargets(PRESET_POLICY.baseRoi, PRESET_POLICY.baseProfit),
      demand: { require_signal: false, min_rating: null, min_reviews: null },
      competition: { max_seller_count: null },
    },
  },
  {
    id: "high-roi",
    name: "High ROI",
    description: "Stronger ROI target with review matches excluded.",
    patch: {
      match_strategy: {
        require_identifier: true,
        allow_title_match: false,
        allow_model_match: true,
        include_review: true,
        allowed_statuses: null,
      },
      exclusions: { review_matches: true },
      profitability: profitTargets(PRESET_POLICY.highRoi, PRESET_POLICY.baseProfit),
      demand: { require_signal: false, min_rating: null, min_reviews: null },
      competition: { max_seller_count: null },
    },
  },
  {
    id: "demand-signal",
    name: "Demand Signal",
    description:
      "Requires a real Walmart rating + review signal with documented floors.",
    patch: {
      match_strategy: {
        require_identifier: false,
        allow_model_match: true,
        allow_title_match: false,
        include_review: true,
        allowed_statuses: null,
      },
      demand: {
        require_signal: true,
        min_rating: PRESET_POLICY.minRating,
        min_reviews: PRESET_POLICY.minReviews,
      },
      competition: { max_seller_count: null },
    },
  },
  {
    id: "low-competition",
    name: "Low Competition",
    description: "Targets listings with few Walmart sellers.",
    patch: {
      competition: { max_seller_count: PRESET_POLICY.maxSellers },
      match_strategy: {
        require_identifier: false,
        allow_model_match: true,
        allow_title_match: false,
        include_review: true,
        allowed_statuses: null,
      },
    },
  },
  {
    id: "needs-review",
    name: "Needs Review",
    description: "Matches that need human review before acting.",
    patch: {
      match_strategy: {
        require_identifier: false,
        allow_model_match: true,
        allow_title_match: false,
        include_review: true,
        allowed_statuses: ["review", "probable"],
      },
    },
  },
];
